import React, { useEffect, useRef } from 'react';
import {
    Animated,
    StyleSheet,
    Platform,
    ViewStyle,
    StyleProp,
    DimensionValue
} from 'react-native';

/**
 * Type-safe Animated style for View
 */
type AnimatedViewStyle = Animated.WithAnimatedObject<ViewStyle>;

export interface SkeletonProps {
    width?: string | number;
    height?: string | number;
    borderRadius?: number;
    style?: StyleProp<ViewStyle>;
    isDark?: boolean;
}

/**
 * Universal Skeleton Component
 * Matches the existing web aesthetic with the standard pulse animation.
 * Platform-agnostic primitives for width/height handling.
 * 100% Type-safe implementation adhering to project mandates.
 */
export const Skeleton: React.FC<SkeletonProps> = ({
    width = '100%',
    height = 20,
    borderRadius = 4,
    style,
    isDark = false,
}) => {
    const pulseValue = useRef(new Animated.Value(0.3)).current;

    useEffect(() => {
        const animation = Animated.loop(
            Animated.sequence([
                Animated.timing(pulseValue, {
                    toValue: 0.7,
                    duration: 1000,
                    easing: (t: number) => t,
                    useNativeDriver: Platform.OS !== 'web',
                }),
                Animated.timing(pulseValue, {
                    toValue: 0.3,
                    duration: 1000,
                    easing: (t: number) => t,
                    useNativeDriver: Platform.OS !== 'web',
                }),
            ])
        );
        animation.start();
        return () => animation.stop();
    }, [pulseValue]);

    const backgroundColor = isDark ? '#374151' : '#E5E7EB';

    const animatedStyle: AnimatedViewStyle = {
        width: width as DimensionValue,
        height: height as DimensionValue,
        borderRadius,
        opacity: pulseValue,
        backgroundColor,
    };

    return (
        <Animated.View
            style={[
                styles.skeleton,
                animatedStyle,
                style as AnimatedViewStyle,
            ]}
        />
    );
};

const styles = StyleSheet.create({
    skeleton: {
        // Shared base styles
    },
});
